import {
  Box,
  Button,
  CircularProgress,
  Icon,
  IconButton,
  InputAdornment,
  Modal,
  OsTextField,
  Slide,
  type Theme,
  Typography,
} from "@outschool/backpack";
import { faSearch, faTimes } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { useLazyQuery, useMutation } from "@outschool/ui-apollo";
import React, { useEffect, useState } from "react";

import type {
  LearnerQuizDeleteLearnerTopicMutation,
  LearnerQuizDeleteLearnerTopicMutationVariables,
  LearnerQuizWriterTopicSearchQuery,
  LearnerQuizWriterTopicSearchQueryVariables,
} from "../../generated/graphql";
import {
  LearnerQuizDeleteLearnerTopic,
  LearnerQuizWriterTopicSearch,
} from "./quizQueries";

export interface QuizWriterMyTopicsDrawerProps {
  open: boolean;
  onClose: () => void;
  onSelectTopic: (topicUid: string, topicName: string) => void;
  refetchTopicSuggestions: () => void | Promise<unknown>;
  isQuizGenerating: boolean;
}

export function QuizWriterMyTopicsDrawer({
  open,
  onClose,
  onSelectTopic,
  refetchTopicSuggestions,
  isQuizGenerating,
}: QuizWriterMyTopicsDrawerProps) {
  const { t } = useTranslation("learnerApp\\Quiz");
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [fetchTopics, { data, loading }] = useLazyQuery<
    LearnerQuizWriterTopicSearchQuery,
    LearnerQuizWriterTopicSearchQueryVariables
  >(LearnerQuizWriterTopicSearch, { fetchPolicy: "network-only" });

  const [deleteTopic, { loading: deleteLoading }] = useMutation<
    LearnerQuizDeleteLearnerTopicMutation,
    LearnerQuizDeleteLearnerTopicMutationVariables
  >(LearnerQuizDeleteLearnerTopic);

  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 300);
    return () => window.clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    if (!open) {
      return;
    }
    void fetchTopics({
      variables: {
        search: debouncedSearch.length > 0 ? debouncedSearch : undefined,
      },
    });
  }, [open, debouncedSearch, fetchTopics]);

  const topics = data?.quizWriterTopicSearch ?? [];

  const runRefresh = async () => {
    await fetchTopics({
      variables: {
        search: debouncedSearch.length > 0 ? debouncedSearch : undefined,
      },
    });
    void refetchTopicSuggestions();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      slotProps={{
        backdrop: {
          sx: { backgroundColor: "rgba(0, 0, 0, 0.45)" },
        },
      }}
      sx={{
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
      }}
    >
      <Slide
        direction="up"
        in={open}
        timeout={{ enter: 280, exit: 220 }}
        mountOnEnter
        unmountOnExit
      >
        <Box
          role="dialog"
          aria-labelledby="quiz-writer-my-topics-title"
          sx={{
            display: "flex",
            flexDirection: "column",
            maxHeight: "min(90vh, 640px)",
            width: "100%",
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            bgcolor: "background.paper",
            outline: "none",
            overflow: "hidden",
            padding: "25px",
          }}
        >
          <Box
            sx={{
              padding: { xs: 2, sm: 3 },
              paddingBottom: 1.5,
              marginBottom: "25px",
            }}
          >
            <Typography
              id="quiz-writer-my-topics-title"
              variant="h5"
              sx={{ fontWeight: 700, marginBottom: "8px" }}
            >
              {t("My Topics")}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {t(
                "Your saved topics. Tap one to start a quiz, or search for something new.",
              )}
            </Typography>
          </Box>
          <Box
            sx={{
              paddingX: { xs: 2, sm: 3 },
              paddingBottom: 2,
              width: "400px",
              marginBottom: "15px",
            }}
          >
            <OsTextField
              ariaLabel={t("Search topics...")}
              size="small"
              fullWidth
              placeholder={t("Search topics...")}
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon icon={faSearch} sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                ),
                endAdornment:
                  loading && debouncedSearch ? (
                    <InputAdornment position="end">
                      <CircularProgress size={16} />
                    </InputAdornment>
                  ) : undefined,
              }}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              paddingX: { xs: 2, sm: 3 },
              paddingBottom: 2,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {loading && topics.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  paddingY: 4,
                }}
              >
                <CircularProgress aria-label={t("Loading topics")} />
              </Box>
            ) : null}
            {!loading && topics.length === 0 ? (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {t("No saved topics yet.")}
              </Typography>
            ) : null}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: "8px",
              }}
            >
              {topics.map(topic => (
                <Box
                  key={topic.uid}
                  sx={(theme: Theme) => ({
                    display: "inline-flex",
                    alignItems: "center",
                    gap: theme.spacing(0.5),
                    maxWidth: "100%",
                    borderRadius: "9999px",
                    padding: ".7rem 1rem",
                    border: `2px solid hsl(254 100% 43% / 0.2)`,
                    backgroundColor: `hsl(254 100% 43% / 0.05)`,
                  })}
                >
                  <Button
                    type="button"
                    variant="text"
                    disabled={isQuizGenerating || deleteLoading}
                    onClick={() => {
                      onSelectTopic(topic.uid, topic.name);
                      onClose();
                    }}
                    sx={{
                      flex: "0 1 auto",
                      width: "auto",
                      minWidth: 0,
                      maxWidth: "100%",
                      padding: 0,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "1.4rem",
                      lineHeight: 1.43,
                      color: "text.primary",
                      justifyContent: "flex-start",
                      "&:hover": {
                        backgroundColor: "transparent",
                        color: "primary.main",
                      },
                      "&.Mui-disabled": {
                        color: "text.disabled",
                      },
                    }}
                  >
                    {topic.name}
                  </Button>
                  <IconButton
                    aria-label={t("Remove {{topicName}}", {
                      topicName: topic.name,
                    })}
                    icon={faTimes}
                    variant="text"
                    disabled={isQuizGenerating || deleteLoading}
                    onClick={async () => {
                      await deleteTopic({
                        variables: { topicUid: topic.uid },
                      });
                      await runRefresh();
                    }}
                    sx={(theme: Theme) => ({
                      flexShrink: 0,
                      width: 28,
                      height: 28,
                      minWidth: 28,
                      color: "text.secondary",
                      "&:hover": {
                        color: "text.primary",
                        backgroundColor: theme.palette.action.hover,
                      },
                    })}
                  />
                </Box>
              ))}
            </Box>
          </Box>
          <Box
            sx={{
              padding: { xs: 2, sm: 3 },
              paddingTop: 1,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Button
              type="button"
              fullWidth
              variant="text"
              onClick={onClose}
              sx={{ fontWeight: 600 }}
            >
              {t("Close")}
            </Button>
          </Box>
        </Box>
      </Slide>
    </Modal>
  );
}
